<template>
  <div class="topbar-item" :class="isViewportMobile() ? 'mx-4' : 'mx-7'">
    <div
      class="btn btn-icon btn-sm btn-clean btn-text-dark-75 quick-toggle-btn"
      id="quick_user_toggle"
      data-test="btn-profile"
      @click="openCanvas"
    >
      <div
        class="rounded-circle d-flex align-items-center justify-content-center cirx-custom-primary-color-bg initials-header-container"
      >
        <p class="m-0 text-white initials-header">{{ initials }}</p>
      </div>
    </div>

    <div id="quick_user" class="offcanvas offcanvas-right p-10" :class="{ 'offcanvas-on': isCanvasOn }">
      <!--begin::Header-->
      <div class="offcanvas-header d-flex align-items-center justify-content-between pb-5">
        <h3 class="font-weight-bold m-0">{{ translate("HEADER.PROFILE.TITLE") }}</h3>
        <a class="btn btn-xs btn-icon btn-light btn-hover-primary" @click="closeCanvas">
          <i class="ki ki-close icon-xs text-muted"></i>
        </a>
      </div>
      <!--end::Header-->

      <perfect-scrollbar class="offcanvas-content pr-5 mr-n5 scroll position-relative" style="max-height: 90vh">
        <div class="d-flex mt-5">
          <div
            class="rounded-circle d-flex align-items-center justify-content-center mr-4 cirx-custom-primary-color-bg text-center initials-quickuser-container"
          >
            <p class="m-0 text-white initials-quickuser">{{ initials }}</p>
          </div>

          <div class="d-flex flex-column cirx-profile-info">
            <span class="font-weight-bold font-size-h5 text-dark-75 cirx-linebreak" data-test="full-name">
              {{ user ? `${user.firstName} ${user.lastName}` : "" }}
            </span>
            <div class="text-muted mt-1 cirx-linebreak" data-test="organization">{{ organization }}</div>
            <div class="navi mt-2">
              <span class="navi-item">
                <span class="navi-link p-0 pb-2">
                  <span class="navi-icon mr-1">
                    <span class="svg-icon svg-icon-lg svg-icon-primary cirx-custom-primary-color-svg">
                      <!--begin::Svg Icon-->
                      <inline-svg src="/media/svg/icons/Communication/Mail-notification.svg" />
                      <!--end::Svg Icon-->
                    </span>
                  </span>
                  <span class="navi-text text-muted cirx-linebreak">
                    {{ user ? user.email : "" }}
                  </span>
                </span>
              </span>
            </div>
            <button
              class="btn btn-light-primary btn-bold cirx-custom-primary-color-btn"
              data-test="btn-logout"
              @click="onLogout"
            >
              {{ translate("HEADER.PROFILE.LOGOUT") }}
            </button>
          </div>
        </div>

        <div v-if="currentOrganization">
          <div class="separator separator-dashed my-7"></div>
          <h5 class="font-weight-bold font-size-h5 text-dark-75 cirx-linebreak mb-7">
            {{ translate("HEADER.MULTI_MODULE.TITLE") }}
          </h5>

          <div v-for="(type, moduleType) in userTypes" :key="moduleType" class="cirx-hover-bg-grey">
            <div class="d-flex flex-row align-items-center p-3 my-3" @click="switchModuleType(moduleType)">
              <div class="symbol symbol-50 bg-light mr-5">
                <div class="symbol-label">
                  <div class="spinner-border spinner-border-lg" role="status" v-if="clicked[moduleType]" />
                  <inline-svg :src="getModuleIconScr(moduleType)" />
                </div>
              </div>

              <div class="w-100">
                <div class="d-flex justify-content-between">
                  <h5 class="text-dark mb-0">
                    {{ translate(`HEADER.MULTI_MODULE.${moduleType.toUpperCase()}`) }}
                  </h5>
                  <span v-if="currentOrganization.moduleType === moduleType" class="font-italic">
                    {{ translate("HEADER.MULTI_MODULE.ACTUAL") }}
                  </span>
                </div>
                <span class="cirx-text-muted" data-test="role">{{ translate(`USER_TYPES.${type}`) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!--end::Header-->
      </perfect-scrollbar>
      <!--end::Content-->
    </div>
    <div class="offcanvas-overlay" v-if="isCanvasOn" @click="closeCanvas"></div>
  </div>
</template>

<script>
import { BRAND } from "@/core/constants/cirxConstants";
import { ROLE_INNER } from "@/core/models/usersAndPermissions";
import { routes } from "@/routing/routes";
import identificationService from "@/core/services/identification.service";
import loginService from "@/core/services/login.service";
import routerDashboard from "@/routing/routerDashboard";
import useTranslate from "@/core/composables/translate";

export default {
  name: "QuickUser",

  inject: ["isViewportMobile"],

  setup() {
    const { translate } = useTranslate();
    return {
      translate,
    };
  },

  data() {
    return {
      user: null,
      userTypes: [],
      currentOrganization: null,
      isCanvasOn: false,
      clicked: { tim: false, lcm: false, rps: false, root: false },
    };
  },

  mounted() {
    this.user = identificationService.getUser();
    this.userTypes = this.user.modules;

    // Don't panic ! Basically, we are filtering values that are null (Object entries + filter)
    // Then, we are going back to an object via Object.fromEntries

    this.userTypes = Object.fromEntries(Object.entries(this.userTypes).filter(([, value]) => value !== null));

    this.currentOrganization = this.user.getCurrentOrganization();
  },

  computed: {
    picture() {
      return "media/users/300_21.jpg";
    },

    organization() {
      if (this.currentOrganization?.commonName) {
        return this.currentOrganization.commonName;
      } else if (identificationService.hasPermission(ROLE_INNER)) {
        return BRAND;
      } else {
        return this.translate("HEADER.PROFILE.NO_ORGANIZATION");
      }
    },

    initials() {
      return String(this.user?.firstName?.charAt(0) + this.user?.lastName?.charAt(0)).toUpperCase();
    },
  },

  methods: {
    onLogout() {
      loginService.logout();
    },
    closeCanvas() {
      this.isCanvasOn = false;
    },
    openCanvas() {
      this.isCanvasOn = true;
    },
    async switchModuleType(moduleType) {
      if (Object.values(this.clicked).find((v) => v === true)) {
        return;
      }

      this.clicked[moduleType] = true;

      await identificationService.switchCurrentOrganization(moduleType);

      if (this.$route.name !== routes.home.name) {
        await routerDashboard.push({ name: routes.home.name });
      }

      await this.$router.go(0);
    },

    getModuleIconScr(moduleType) {
      return identificationService.getModuleIconScr(moduleType);
    },
  },
};
</script>

<style lang="scss" scoped>
#quick_user {
  overflow: hidden;
}

.cirx-profile-info {
  width: 100%;
}

.cirx-linebreak {
  overflow-wrap: anywhere;
}

.cirx-hover-bg-grey:hover {
  cursor: pointer;
  background: #eaeaea;
  border-radius: 10px;
}

@media (min-width: 991px) {
  .initials-header-container {
    min-height: 50px;
    max-height: 50px;
    min-width: 50px;
    max-width: 50px;
  }

  .initials-header {
    font-size: 20px;
  }
}

@media (max-width: 990px) {
  .initials-header-container {
    min-height: 35px;
    max-height: 35px;
    min-width: 35px;
    max-width: 35px;
  }

  .initials-header {
    font-size: 16px;
  }
}

.initials-quickuser-container {
  min-width: 5rem;
  max-width: 5rem;
  min-height: 5rem;
  max-height: 5rem;
}

.initials-quickuser {
  font-size: 2rem;
  line-height: 3.5rem;
}
</style>
